import { mergePairs } from 'dive-common/typeHierarchy';
import { syncHeadTail } from './headTail';
import { RectBounds, polygonEqualsBounds } from './utils';
import {
  binarySearch,
  listInsert,
  getSurroundingElements,
  listRemove,
} from './listUtils';
import BaseAnnotation, {
  AnnotationId, BaseAnnotationParams, BaseData, StringKeyObject,
} from './BaseAnnotation';

export type InterpolateFeatures = [Feature | null, Feature | null, Feature | null];
/** @deprecated use AnnotationId instead */
export type TrackId = number;
export type TrackSupportedFeature = (
  GeoJSON.Point | GeoJSON.Polygon | GeoJSON.LineString | GeoJSON.Point);

/* Frame feature for both TrackData and Track */
export interface Feature {
  frame: number;
  flick?: Readonly<number>;
  interpolate?: boolean;
  keyframe?: boolean;
  bounds?: RectBounds;
  geometry?: GeoJSON.FeatureCollection<TrackSupportedFeature>;
  fishLength?: number;
  attributes?: StringKeyObject & { userAttributes?: StringKeyObject };
  head?: [number, number];
  tail?: [number, number];
  notes?: string[];
}

/** TrackData is the json schema for Track transport */
export interface TrackData extends BaseData {
  features: Array<Feature>;
}

/* Constructor params for Track */
interface TrackParams extends BaseAnnotationParams {
  features?: Array<Feature>;
  set?: string;
}

/**
 * Track manages the state of a track, its
 * frame data, and all metadata.
 */
export default class Track extends BaseAnnotation {
  /* Sparse array maps frame # to feature object */
  features: Feature[];

  /**
   * Sorted dense array of frame indices into this.features
   * for performing fast search */
  featureIndex: number[];

  /**
   * Set used for when making comparisons between sets of tracks
   */
  set?: string;

  constructor(id: AnnotationId, params: TrackParams) {
    super(id, params);
    this.features = params.features || []; // NON-reactive sparse array
    this.featureIndex = [];
    Track.sanityCheckFeatures(this.features);
    this.repopulateInterpolatedFrames(this.features);
    if (params.set) {
      this.set = params.set;
    }
  }

  /**
   * @deprecated Use id instead.
   */
  public get trackId() {
    return this.id;
  }

  /**
   * True after at least 1 feature has been added
   */
  protected isInitialized() {
    return this.featureIndex.length > 0;
  }

  /**
   * Test the first element in the feature array.  Its index should match
   * its frame number.  Otherwise, the constructor was called with a
   * dense array, which is incorrect.
   */
  private static sanityCheckFeatures(features: Feature[]) {
    const breakException = Symbol('breakException');
    try {
      features.forEach((f, i) => {
        if (f.frame !== i) {
          throw new Error(
            'features must be initialized with sparse array.'
            + 'Use Track.fromJson() if you want to initialize with features.',
          );
        }
        throw breakException;
      });
    } catch (e) {
      if (e !== breakException) throw e;
    }
  }

  private repopulateInterpolatedFrames(features: Feature[]) {
    this.featureIndex = [];
    features.forEach((f) => {
      // TODO: Figure out what the conditions are for this.
      if (f.keyframe && f.bounds) {
        this.featureIndex.push(f.frame);
      }
      if (!!f.keyframe !== !!f.bounds) {
        throw new Error('keyframe must not XOR bounds');
      }
    });
  }

  /* Call if the bounds were possible shrunk */
  private maybeShrinkBounds(frame: number) {
    const oldval = [this.begin, this.end];
    if (frame === this.begin) {
      // frame is begin
      const nextFrame = this.getNextKeyframe(this.begin + 1);
      if (nextFrame === undefined) {
        this.begin = Infinity;
        this.end = 0;
      } else {
        this.begin = nextFrame;
      }
      this.notify('bounds', oldval);
    } else if (frame === this.end) {
      // frame is end
      const previousFrame = this.getPreviousKeyframe(this.end - 1);
      if (previousFrame === undefined) {
        this.end = 0;
        this.begin = Infinity;
      } else {
        this.end = previousFrame;
      }
      this.notify('bounds', oldval);
    }
  }

  /** Determine if track can be split at frame */
  canSplit(frame: number) {
    return frame > this.begin && frame <= this.end;
  }

  /** Determine if a hypothetical feature at frame should enable interpolation */
  canInterpolate(frame: number): {
    features: InterpolateFeatures;
    interpolate: boolean;
  } {
    const [real, lower, upper] = this.getFeature(frame);
    return {
      features: [real, lower, upper],
      interpolate: real?.interpolate
        || (lower?.interpolate)
        || (!lower && (upper?.interpolate || false)),
    };
  }

  /**
   * Split trackId in two at given frame, where frame is allocated
   * to the second track.  Both tracks must end up with at least 1 detection.
   */
  split(frame: number, id1: AnnotationId, id2: AnnotationId): [Track, Track] {
    if (!this.canSplit(frame)) {
      throw new Error(`Cannot split track ${this.id} at frame ${frame}.  Frame bounds are [${this.begin}, ${this.end}]`);
    }
    return [
      Track.fromJSON({
        id: id1,
        meta: this.meta,
        begin: this.begin,
        end: this.getPreviousKeyframe(frame - 1) || this.begin,
        features: this.features.slice(this.begin, frame),
        confidencePairs: this.confidencePairs
          .map(([type, confidence]) => [type, confidence] as [string, number]),
        attributes: this.attributes,
      }),
      Track.fromJSON({
        id: id2,
        meta: this.meta,
        begin: this.getNextKeyframe(frame) || this.end,
        end: this.end,
        features: this.features.slice(frame),
        confidencePairs: this.confidencePairs
          .map(([type, confidence]) => [type, confidence] as [string, number]),
        attributes: this.attributes,
      }),
    ];
  }

  /**
   * Merge other into track at frame, preferring features from
   * self if there are conflicts
   */
  merge(others: Track[]) {
    const previousPairs = this.confidencePairs;
    const mergedPairs = mergePairs([
      this.confidencePairs,
      ...others.map((other) => other.confidencePairs),
    ]);
    const pairsChanged = mergedPairs.length !== previousPairs.length
      || mergedPairs.some(([type, confidence], index) => (
        previousPairs[index]?.[0] !== type || previousPairs[index]?.[1] !== confidence
      ));
    if (pairsChanged) {
      this.confidencePairs = mergedPairs;
      this.notify('confidencePairs', previousPairs);
    }
    others.forEach((other) => {
      other.features.forEach((f) => {
        if (this.getFeature(f.frame)[0] === null) {
          this.setFeature(f, f.geometry?.features);
        }
      });
      const { attributes } = other;
      if (attributes !== undefined) {
        Object.entries(attributes).forEach(([key, val]) => {
          if (([null, undefined] as unknown[]).indexOf(this.attributes[key]) !== -1) {
            this.setAttribute(key, val);
          }
        });
      }
    });
  }

  toggleKeyframe(frame: number) {
    const { features } = this.canInterpolate(frame);
    const [real, lower, upper] = features;
    if (real && this.length === 1) {
      throw new Error(`This is the only keyframe in Track:${this.trackId} it cannot be removed`);
    }
    if (real && !real.keyframe) {
      this.setFeature({
        ...real,
        frame,
        keyframe: true,
      });
    } else if ((lower || upper) && !real?.keyframe) {
      let interFeature: Feature | null = null;
      if (upper && frame > upper.frame) {
        interFeature = upper;
      } else if (lower && frame < lower.frame) {
        interFeature = lower;
      }
      if (interFeature) {
        this.setFeature({
          ...interFeature,
          frame,
          keyframe: true,
        });
      }
    } else if (real?.keyframe) {
      this.deleteFeature(frame);
    }
  }

  toggleInterpolation(frame: number) {
    const { features, interpolate } = this.canInterpolate(frame);
    const [real, lower, upper] = features;
    const targetKeyframe = real?.keyframe ? real : (lower || upper);
    if (targetKeyframe) {
      this.setFeature({
        ...targetKeyframe,
        interpolate: !interpolate,
      });
    }
  }

  toggleInterpolationForAllGaps(frame: number): void {
    const { interpolate } = this.canInterpolate(frame);

    for (let i = this.begin; i < this.end; i += 1) {
      const { features } = this.canInterpolate(i);
      const [real, lower, upper] = features;
      const targetKeyframe = real?.keyframe ? real : (lower || upper);
      if (targetKeyframe) {
        this.setFeature({
          ...targetKeyframe,
          interpolate: !interpolate,
        });
      }
    }
  }

  setFeature(feature: Feature, geometry: GeoJSON.Feature<TrackSupportedFeature>[] = []): Feature {
    const f = this.features[feature.frame] || {};
    const oldLine = f.geometry?.features.find((g) => g.properties?.key === 'HeadTails' && g.geometry.type === 'LineString');
    const oldCoordinates = JSON.stringify(oldLine?.geometry);
    this.features[feature.frame] = {
      ...f,
      ...feature,
    };
    // round bounds if necessary
    if (feature.bounds) {
      this.features[feature.frame].bounds = [
        Math.round(feature.bounds[0]),
        Math.round(feature.bounds[1]),
        Math.round(feature.bounds[2]),
        Math.round(feature.bounds[3]),
      ];
    }
    if (!this.features[feature.frame].keyframe) {
      throw new Error('setFeature must be called with keyframe=true OR to update an existing keyframe');
    }
    listInsert(this.featureIndex, feature.frame);
    const fg = this.features[feature.frame].geometry || { type: 'FeatureCollection', features: [] };
    geometry.forEach((geo) => {
      const i = fg.features
        .findIndex((item) => {
          // Compare keys directly, treating undefined/null as empty string
          const geoKey = geo.properties?.key ?? '';
          const itemKey = item.properties?.key ?? '';
          const keyMatch = geoKey === itemKey;
          const typeMatch = item.geometry.type === geo.geometry.type;
          return keyMatch && typeMatch;
        });
      if (i >= 0) {
        fg.features.splice(i, 1, geo);
      } else {
        fg.features.push(geo);
      }
    });
    if (fg.features.length) {
      fg.features = syncHeadTail(fg.features);
      const current = this.features[feature.frame];
      const line = fg.features.find((g) => g.properties?.key === 'HeadTails' && g.geometry.type === 'LineString');
      if (line?.geometry.type === 'LineString') {
        current.head = [...line.geometry.coordinates[0]] as [number, number];
        current.tail = [...line.geometry.coordinates[line.geometry.coordinates.length - 1]] as [number, number];
      }
      if (oldLine && oldCoordinates !== JSON.stringify(line?.geometry)) {
        current.attributes = { ...current.attributes, measurement_stale: true };
        delete this.attributes.avg_length;
        // Keep explicitly user-entered lengths; derived results must be recomputed.
        if (current.attributes.length_method !== 'user_set') {
          current.fishLength = undefined;
          ['length', 'curved_length', 'straight_length', 'curvature_ratio', 'avg_length',
            'midpoint_x', 'midpoint_y', 'midpoint_z', 'midpoint_range', 'stereo_rms']
            .forEach((key) => { delete current.attributes?.[key]; });
        }
      }
      this.features[feature.frame].geometry = fg;
    }
    this.maybeExpandBounds(feature.frame);
    if (this.featureIndex.length === 1) {
      /**
       * If this is the very first feature, it may be necessary
       * to shrink the bounds if the first feature was added on a different frame
       * than the track was created on
       */
      if (feature.frame !== this.begin) {
        this.maybeShrinkBounds(this.begin);
      } else if (feature.frame !== this.end) {
        this.maybeShrinkBounds(this.end);
      }
    }
    this.notify('feature', f);
    return this.features[feature.frame];
  }

  /** Clear derived results after a geometry edit on either stereo camera. */
  invalidateMeasurement(frame: number) {
    const feature = this.features[frame];
    if (!feature) return;
    const attributes = { ...feature.attributes, measurement_stale: true } as StringKeyObject;
    const locked = attributes.length_method === 'user_set';
    ['curved_length', 'straight_length', 'curvature_ratio', 'avg_length',
      'midpoint_x', 'midpoint_y', 'midpoint_z', 'midpoint_range', 'stereo_rms']
      .forEach((key) => { delete attributes[key]; });
    if (!locked) delete attributes.length;
    this.setFeature({ frame, attributes, fishLength: locked ? feature.fishLength : undefined });
    delete this.attributes.avg_length;
  }

  /* Get features by properties.key, geometry.type, or both */
  getFeatureGeometry(frame: number, { key, type }:
    { key?: string; type?: GeoJSON.GeoJsonGeometryTypes | '' | 'rectangle' }) {
    const feature = this.features[frame];
    if (!feature || !feature.geometry) {
      return [];
    }
    return feature.geometry.features.filter((item) => {
      // Check key match: undefined means match all, otherwise compare (treating undefined/null as '')
      const matchesKey = key === undefined
        || (item.properties?.key ?? '') === key;
      const matchesType = !type || item.geometry.type === type;
      return matchesKey && matchesType;
    });
  }

  removeFeatureGeometry(frame: number, { key, type }:
    { key?: string; type?: GeoJSON.GeoJsonGeometryTypes | '' | 'rectangle' }) {
    const feature = this.features[frame];
    if (!feature.geometry) {
      return false;
    }
    const index = feature.geometry.features.findIndex((item) => {
      // Check key match: undefined means match all, otherwise compare (treating undefined/null as '')
      const matchesKey = key === undefined
        || (item.properties?.key ?? '') === key;
      const matchesType = !type || item.geometry.type === type;
      return matchesKey && matchesType;
    });
    if (index !== -1) {
      feature.geometry.features.splice(index, 1);
      this.notify('feature', feature);
      return true;
    }
    return false;
  }

  setFeatureNotes(frame: number, notes: string) {
    const notesArray = notes.trim() ? [notes.trim()] : undefined;
    // Try exact frame first (most common case - using track.begin)
    if (this.features[frame]) {
      this.features[frame].notes = notesArray;
      this.notify('feature', this.features[frame]);
      return;
    }
    // Otherwise find the nearest keyframe (prefer previous/lower)
    const [, lower, upper] = this.getFeature(frame);
    const targetFeature = lower || upper;
    if (targetFeature) {
      targetFeature.notes = notesArray;
      this.notify('feature', targetFeature);
    }
  }

  /**
   * Get all polygon features for a frame
   * @returns Array of polygon GeoJSON features with their keys
   */
  getPolygonFeatures(frame: number): Array<{
    key: string;
    geometry: GeoJSON.Polygon;
    hasHoles: boolean;
    holeCount: number;
  }> {
    const feature = this.features[frame];
    if (!feature?.geometry) {
      return [];
    }
    const polygons: Array<{
      key: string;
      geometry: GeoJSON.Polygon;
      hasHoles: boolean;
      holeCount: number;
    }> = [];
    feature.geometry.features.forEach((item) => {
      if (item.geometry.type === 'Polygon') {
        const coords = item.geometry.coordinates as GeoJSON.Position[][];
        polygons.push({
          key: item.properties?.key || '',
          geometry: item.geometry,
          hasHoles: coords.length > 1,
          holeCount: Math.max(0, coords.length - 1),
        });
      }
    });
    return polygons;
  }

  /**
   * Add a hole to an existing polygon
   * @param frame frame number
   * @param key polygon key to add hole to
   * @param holeCoords coordinates of the hole (array of [x,y] positions)
   * @returns true if hole was added successfully
   */
  addHoleToPolygon(frame: number, key: string, holeCoords: GeoJSON.Position[]): boolean {
    const feature = this.features[frame];
    if (!feature?.geometry) {
      return false;
    }
    const polygonFeature = feature.geometry.features.find(
      (item) => item.geometry.type === 'Polygon' && (item.properties?.key ?? '') === key,
    );
    if (polygonFeature && polygonFeature.geometry.type === 'Polygon') {
      (polygonFeature.geometry.coordinates as GeoJSON.Position[][]).push(holeCoords);
      this.notify('feature', feature);
      return true;
    }
    return false;
  }

  /**
   * Remove a hole from a polygon
   * @param frame frame number
   * @param key polygon key
   * @param holeIndex index of the hole to remove (0 = first hole, which is coordinates[1])
   * @returns true if hole was removed successfully
   */
  removeHoleFromPolygon(frame: number, key: string, holeIndex: number): boolean {
    const feature = this.features[frame];
    if (!feature?.geometry) {
      return false;
    }
    const polygonFeature = feature.geometry.features.find(
      (item) => item.geometry.type === 'Polygon' && (item.properties?.key ?? '') === key,
    );
    if (polygonFeature && polygonFeature.geometry.type === 'Polygon') {
      const coords = polygonFeature.geometry.coordinates as GeoJSON.Position[][];
      // holeIndex 0 corresponds to coords[1], holeIndex 1 to coords[2], etc.
      const actualIndex = holeIndex + 1;
      if (actualIndex > 0 && actualIndex < coords.length) {
        coords.splice(actualIndex, 1);
        this.notify('feature', feature);
        return true;
      }
    }
    return false;
  }

  /**
   * Get the next available polygon key for this frame
   * @param frame frame number
   * @returns next available key (e.g., "1", "2", etc.)
   */
  getNextPolygonKey(frame: number): string {
    const polygons = this.getPolygonFeatures(frame);
    if (polygons.length === 0) {
      return '';
    }
    // Find the highest numeric key and increment
    let maxKey = 0;
    polygons.forEach((p) => {
      if (p.key === '') {
        maxKey = Math.max(maxKey, 0);
      } else {
        const numKey = parseInt(p.key, 10);
        if (!Number.isNaN(numKey)) {
          maxKey = Math.max(maxKey, numKey);
        }
      }
    });
    return String(maxKey + 1);
  }

  setFeatureAttribute(frame: number, name: string, value: unknown, user: null | string = null) {
    if (this.features[frame]) {
      if (user !== null) {
        this.features[frame].attributes = {
          ...this.features[frame].attributes,
        };
        if (this.features[frame].attributes !== undefined
          && (this.features[frame].attributes as StringKeyObject).userAttributes === undefined) {
          (this.features[frame].attributes as StringKeyObject).userAttributes = {};
        }
        ((this.features[frame].attributes as StringKeyObject)
          .userAttributes as StringKeyObject)[user] = {
          ...(this.features[frame].attributes as StringKeyObject)[user] as StringKeyObject,
          [name]: value,
        };
      } else {
        this.features[frame].attributes = {
          ...this.features[frame].attributes,
          [name]: value,
        };
      }
      this.notify('feature', this.features[frame]);
    }
  }

  /**
   * Returns a 3-tuple of nullable features:
   * [exact_feature_match, previous_keyframe, next_keyframe]
   */
  getFeature(frame: number): [Feature | null, Feature | null, Feature | null] {
    return Track.getFeatureFrom(this.features, this.featureIndex, this.begin, this.end, frame);
  }

  static getFeatureFrom(
    features: readonly (Feature | undefined)[],
    featureIndex: readonly number[],
    begin: number,
    end: number,
    frame: number,
  ): [Feature | null, Feature | null, Feature | null] {
    const maybeFrame = features[frame];
    if (maybeFrame) {
      return [maybeFrame, maybeFrame, maybeFrame];
    }
    if (frame < begin || frame > end) {
      if (frame <= begin) {
        return [null, features[begin] as Feature, null];
      }
      return [null, null, features[end] as Feature];
    }
    const position = binarySearch(featureIndex, frame);
    const maybeInterpolated = getSurroundingElements(featureIndex, position);

    if (maybeInterpolated !== null) {
      const [d0, d1] = maybeInterpolated.map((_frame) => features[_frame]);
      return [Track.interpolate(frame, d0 as Feature, d1 as Feature), d0 as Feature, d1 as Feature];
    }

    if (featureIndex.length !== 0) {
      throw new Error(`Unexpected condition: Track bounds mis-aligned with feature array.
        begin=${begin}
        end=${end}
        firstFeature=${featureIndex[0]}
      `);
    }
    return [null, null, null];
  }

  /* Given a frame number, find the track's next keyframe */
  getNextKeyframe(frame: number): number | undefined {
    const next = this.features.slice(frame).find((a) => a);
    return next?.frame;
  }

  /* Given a frame number, find the track's previous keyframe */
  getPreviousKeyframe(frame: number): number | undefined {
    const previous = this.features.slice(0, frame + 1).reverse().find((a) => a);
    return previous?.frame;
  }

  deleteFeature(frame: number) {
    const feature = this.features[frame];
    if (feature && feature.keyframe) {
      listRemove(this.featureIndex, frame);
    }
    delete this.features[frame];
    this.maybeShrinkBounds(frame);
    this.notify('feature', feature);
  }

  /* Condense the sparse array to a dense one */
  condenseFeatures(): Feature[] {
    const features = [] as Feature[];
    this.features.forEach((f) => {
      features.push(f);
    });
    return features;
  }

  getUserAttributeList() {
    const userList = new Set<string>();
    if (this.attributes && this.attributes.userAttributes) {
      Object.keys(this.attributes.userAttributes).forEach((item) => userList.add(item));
    }
    if (this.features) {
      this.features.forEach((feature) => {
        if (feature.attributes && feature.attributes.userAttributes) {
          Object.keys(feature.attributes.userAttributes).forEach((item) => userList.add(item));
        }
      });
    }
    return userList;
  }

  /* Serialize back to a regular track object */
  serialize(): TrackData {
    return {
      id: this.id,
      meta: this.meta,
      attributes: this.attributes,
      confidencePairs: this.confidencePairs,
      features: this.condenseFeatures(),
      begin: this.begin,
      end: this.end,
    };
  }

  /* Interpolate feature from d0 to d1 @ frame */
  static interpolate(frame: number, d0: Feature, d1: Feature): Feature | null {
    if (!d0.interpolate) {
      return null;
    }
    const len = d1.frame - d0.frame;
    // a + b = 1; interpolate from a to b
    const b = Math.abs((frame - d0.frame) / len);
    const a = 1 - b;
    let keyframe = false;
    if (b === 0 || a === 0) {
      keyframe = true; // actually this is a keyframe
    }
    let box;
    if (d0.bounds && d1.bounds) {
      const d0bounds = d0.bounds;
      const d1bounds = d1.bounds;
      box = d0bounds.map((_, i) => ((d0bounds[i] * a) + (d1bounds[i] * b)));
    } else {
      throw new Error('Bounds cannot be missing from interpolated features');
    }
    return {
      frame,
      bounds: [box[0], box[1], box[2], box[3]],
      interpolate: true,
      keyframe,
    };
  }

  static fromJSON(json: TrackData, set?: string): Track {
    const sparseFeatures: Array<Feature> = [];
    json.features.forEach((f) => {
      if (f === null || f === undefined) return;
      const feature: Feature = {
        keyframe: true,
        ...f,
      };
      // A polygon that is just the full detection box carries no information
      // beyond the box itself and makes editing harder; drop such polygons
      // when loading.
      if (feature.bounds && feature.geometry?.features?.length) {
        const kept = feature.geometry.features.filter((geo) => !(
          geo.geometry?.type === 'Polygon'
          && polygonEqualsBounds(geo.geometry, feature.bounds as RectBounds)
        ));
        if (kept.length !== feature.geometry.features.length) {
          feature.geometry = kept.length
            ? { ...feature.geometry, features: kept }
            : undefined;
        }
      }
      if (feature.geometry?.features.some((g) => g.properties?.key === 'HeadTails')) {
        feature.geometry = { ...feature.geometry, features: syncHeadTail(feature.geometry.features) };
      }
      sparseFeatures[f.frame] = feature;
    });
    // accept either number or string, convert to number
    const intTrackId = parseInt(json.id.toString(), 10);
    const track = new Track(intTrackId, {
      features: sparseFeatures,
      meta: json.meta,
      attributes: json.attributes,
      confidencePairs: json.confidencePairs,
      begin: json.begin,
      end: json.end,
      set,
    });
    return track;
  }
}
